/**
 * [⋯] 메뉴 — 자주 쓰지 않거나 되돌리기 어려운 작업을 한 단추 뒤로 접는다(점진적 공개).
 *
 * 줄마다 [편집·복제·중지·삭제] 를 같은 무게로 늘어놓으면 눈이 작업 단추에 걸려 목록이 읽히지 않고,
 * 빨간 삭제가 늘 손 닿는 자리에 있다. 주 작업 하나만 밖에 두고 나머지를 여기에 넣는다.
 *
 * - **위험한 작업은 맨 아래**, 구분선 뒤에 빨갛게 — 넣은 순서와 관계없이(`orderMenuItems`).
 * - 화살표·Home·End 로 고르고 Esc 로 닫으면 초점이 단추로 돌아온다(WAI-ARIA menu 버튼).
 * - 표 줄 안에서 쓰므로 단추를 눌러도 줄의 클릭(상세 열기)으로 번지지 않는다.
 */
import { MoreHorizontal } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

export interface MoreMenuItem {
  label: string;
  onSelect: () => void;
  /** 되돌릴 수 없거나 화면을 멈추는 작업 — 맨 아래 구분선 뒤에 빨갛게. */
  danger?: boolean;
  disabled?: boolean;
  /** 못 누르는 까닭 같은 짧은 말 — 마우스를 올리면 보인다. */
  title?: string;
  /** false 면 아예 넣지 않는다 — 권한이 없는 작업을 조건부 배열 없이 적으려고. */
  shown?: boolean;
}

/** 보일 항목만, 위험한 것은 맨 아래로(각 묶음 안의 순서는 그대로). */
export function orderMenuItems(items: readonly MoreMenuItem[]): { safe: MoreMenuItem[]; danger: MoreMenuItem[] } {
  const visible = items.filter((item) => item.shown !== false);
  return { safe: visible.filter((item) => !item.danger), danger: visible.filter((item) => item.danger) };
}

const GAP = 4;
const EDGE = 8;

/** 메뉴 자리 — 단추 오른쪽 끝에 맞춰 아래로, 아래가 모자라면 위로. 화면 밖으로 나가지 않게 민다. */
export function placeMenu(
  anchor: { top: number; bottom: number; left: number; right: number },
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
): { top: number; left: number } {
  const below = anchor.bottom + GAP;
  const above = anchor.top - GAP - menu.height;
  const top = below + menu.height <= viewport.height - EDGE || above < EDGE ? below : above;
  const left = Math.max(EDGE, Math.min(anchor.right - menu.width, viewport.width - menu.width - EDGE));
  return { top, left };
}

/** 화살표로 움직일 다음 칸 — 못 누르는 항목은 건너뛰고 끝에서 처음으로 돈다. 고를 것이 없으면 -1. */
export function nextMenuIndex(enabled: readonly boolean[], from: number, step: 1 | -1): number {
  const count = enabled.length;
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (((from + step * offset) % count) + count) % count;
    if (enabled[index]) return index;
  }
  return -1;
}

export function MoreMenu({
  items,
  label = '더 보기',
  size = 'tiny',
}: {
  items: readonly MoreMenuItem[];
  /**
   * 화면 낭독기에 읽히는 이름. 무엇의 메뉴인지 적는다(예: "가을 캠페인 작업"). 툴팁은 늘 "더 보기" 다 — 이 이름을 툴팁에도
   * 띄웠더니 줄마다 "test 작업" 처럼 이름 뒤에 '작업' 만 붙은 말이 떠 무슨 단추인지 알 수 없었다(2026-10-20).
   */
  label?: string;
  size?: 'tiny' | 'normal';
}): ReactNode {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const { safe, danger } = orderMenuItems(items);
  const all = [...safe, ...danger];

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (refocus) anchor.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !anchor.current || !menu.current) return;
    const a = anchor.current.getBoundingClientRect();
    const b = menu.current.getBoundingClientRect();
    const next = placeMenu(a, { width: b.width, height: b.height }, { width: window.innerWidth, height: window.innerHeight });
    setPosition((current) => (current && current.top === next.top && current.left === next.left ? current : next));
  });

  useEffect(() => {
    if (!open) return;
    const buttons = (): HTMLButtonElement[] => [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    buttons().find((button) => !button.disabled)?.focus();
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node && (anchor.current?.contains(target) === true || menu.current?.contains(target) === true);
    const onPointer = (event: PointerEvent): void => {
      if (!inside(event.target)) close(false);
    };
    const onDismiss = (): void => close(false);
    document.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [open, close]);

  const onMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    // 포털이어도 React 이벤트는 줄(tr)까지 번진다 — 줄의 Enter(상세 열기)가 같이 돌지 않게.
    event.stopPropagation();
    const buttons = [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    const enabled = buttons.map((button) => !button.disabled);
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    let target = -1;
    if (event.key === 'ArrowDown') target = nextMenuIndex(enabled, current, 1);
    else if (event.key === 'ArrowUp') target = nextMenuIndex(enabled, current < 0 ? 0 : current, -1);
    else if (event.key === 'Home') target = nextMenuIndex(enabled, -1, 1);
    else if (event.key === 'End') target = nextMenuIndex(enabled, 0, -1);
    else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      close(true);
      return;
    } else return;
    event.preventDefault();
    if (target >= 0) buttons[target]?.focus();
  };

  if (all.length === 0) return null;

  const row = (item: MoreMenuItem): ReactNode => (
    <button
      key={item.label}
      type="button"
      role="menuitem"
      className={`more-menu__item${item.danger ? ' is-danger' : ''}`}
      disabled={item.disabled}
      title={item.title}
      onClick={() => {
        close(true);
        item.onSelect();
      }}
    >
      {item.label}
    </button>
  );

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`btn btn-icon${size === 'tiny' ? ' btn-tiny' : ''} more-menu__trigger`}
        aria-label={label}
        title="더 보기"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close(false);
          else setOpen(true);
        }}
        onKeyDown={(event) => {
          // 줄의 Enter(상세 열기)로 번지지 않게.
          event.stopPropagation();
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <MoreHorizontal className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              id={id}
              role="menu"
              aria-label={label}
              className="more-menu"
              style={position ?? { top: -9999, left: -9999 }}
              onKeyDown={onMenuKey}
              onClick={(event) => event.stopPropagation()}
            >
              {safe.map(row)}
              {safe.length > 0 && danger.length > 0 ? <div className="more-menu__sep" role="separator" /> : null}
              {danger.map(row)}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
